import type { FC } from "react";
import {
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { ChipProps } from "@mui/material/Chip";
import { CustomCellDate, ErrorMessage } from "@/shared/components";
import { useDisputesQuery } from "@/admin/entities/disputes";

interface PaymentDisputesProps {
  paymentId: string;
}

// Тот же локальный маппинг статусов, что и в feature disputes — admin
// не переиспользует partner-локализацию, здесь нет i18n.
const statusChipConfig: Record<
  string,
  { label: string; color: ChipProps["color"] }
> = {
  NEEDS_RESPONSE: { label: "Требует ответа", color: "warning" },
  WON: { label: "Выигран", color: "success" },
  LOST: { label: "Проигран", color: "error" },
  CLOSED: { label: "Закрыт", color: "default" },
};

export const PaymentDisputes: FC<PaymentDisputesProps> = ({ paymentId }) => {
  const { data, loading, error } = useDisputesQuery({
    variables: { filter: { paymentId } },
    fetchPolicy: "cache-and-network",
  });

  const nodes = data?.disputes?.nodes ?? [];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          Споры
        </Typography>

        {error && <ErrorMessage error={error} />}
        {loading && !data && <LinearProgress />}

        {!loading && !error && nodes.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Споров по этому платежу нет
          </Typography>
        )}

        {nodes.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Сумма</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Закрыт</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nodes.map((dispute) => {
                const { label, color } = statusChipConfig[dispute.status] ?? {
                  label: dispute.status,
                  color: "default" as const,
                };
                return (
                  <TableRow key={dispute.id}>
                    <TableCell>
                      <CustomCellDate date={dispute.createdAt as string} />
                    </TableCell>
                    <TableCell>
                      {dispute.amount.value} {dispute.amount.currency}
                    </TableCell>
                    <TableCell>
                      <Chip label={label} color={color} size="small" />
                    </TableCell>
                    <TableCell>
                      {dispute.closedAt ? (
                        <CustomCellDate date={dispute.closedAt as string} />
                      ) : (
                        ""
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
